import React from 'react';
import { motion } from 'framer-motion';
import { Award, Shield, Users, Target, CheckCircle, TrendingUp } from 'lucide-react';

const AboutUs = () => {
  const openLeadForm = () => {
    window.dispatchEvent(new CustomEvent('openLeadForm'));
  };

  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We uphold the highest ethical standards in every case we handle.'
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for exceptional results and innovative legal solutions.'
    },
    {
      icon: Users,
      title: 'Client Focus',
      description: 'Your success is our priority. We listen, understand, and deliver.'
    },
    {
      icon: Award,
      title: 'Expertise',
      description: 'Extensive experience in corporate and IP law.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1664289262417-93f2d037adff?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Law firm office building"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Tiller Kingsley Bale Associates</h1>
            <p className="text-xl md:text-2xl text-slate-300">
              Providing expert legal counsel and representation for businesses and individuals for over three decades.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  Tiller Kingsley Bale Associates has grown into one of the region's 
                  most respected law firms. Our founders recognized the need for sophisticated legal services that combine 
                  deep expertise with personalized attention.
                </p>
                <p>
                  Today, we provide comprehensive legal services across corporate law, business transactions, intellectual 
                  property, immigration, and regulatory compliance. Our team of experienced attorneys brings together diverse 
                  backgrounds and specialized knowledge to deliver exceptional results.
                </p>
                <p>
                  We've successfully represented major corporations, growing startups, non-profit organizations, and 
                  individuals navigating complex legal challenges. Our reputation is built on integrity, expertise, and an 
                  unwavering commitment to our clients' success.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1735723586476-61a6d0949fb0?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Law firm building"
                className="w-full h-[500px] object-cover shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              Guiding principles that shape our practice and client relationships
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-8 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white border border-slate-100 shadow-sm p-8 text-center flex-shrink-0 w-[85vw] md:w-auto snap-center"
                >
                  <Icon className="h-12 w-12 text-amber-700 mx-auto mb-4" />
                  <h3 className="text-2xl font-medium text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">Comprehensive Legal Expertise</h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
              Our firm provides sophisticated legal counsel across multiple practice areas, serving diverse clients across all industries
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="border-l-4 border-amber-700 pl-6">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Corporate & Business Law</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We guide businesses through every stage of their lifecycle, from formation and financing to mergers, acquisitions, and complex restructurings. Our corporate attorneys understand the intricacies of business transactions and provide strategic counsel that protects your interests while advancing your business objectives.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Our services include entity formation, corporate governance, contract negotiation, M&A transactions, securities offerings, and business succession planning. We work closely with management teams and boards of directors to navigate complex legal and regulatory challenges.
                </p>
              </div>

              <div className="border-l-4 border-amber-700 pl-6">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Intellectual Property</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  In today's knowledge economy, protecting intellectual property is essential to business success. Our IP attorneys help clients secure, manage, and enforce their intellectual property rights through trademark and copyright registration, patent counseling, licensing agreements, and litigation.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We develop comprehensive IP strategies that align with business goals, conduct clearance searches, file applications, manage prosecution, and defend against infringement. Our team has extensive experience across technology, entertainment, consumer products, and professional services sectors.
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <div className="border-l-4 border-amber-700 pl-6">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Regulatory Compliance</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Navigating the complex regulatory landscape requires proactive compliance strategies and experienced counsel. We help organizations develop and implement comprehensive compliance programs that meet regulatory requirements while supporting business operations.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Our regulatory practice includes anti-corruption compliance, government contracting, regulatory investigations, and white-collar defense. We conduct internal investigations, develop policies and procedures, provide training, and represent clients before regulatory agencies and in enforcement proceedings.
                </p>
              </div>

              <div className="border-l-4 border-amber-700 pl-6">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Immigration Services</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Global businesses rely on international talent, and we help companies and individuals navigate the complex U.S. immigration system. Our immigration attorneys handle employment-based visas, green cards, naturalization, and compliance matters with efficiency and expertise.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We work with HR departments to develop immigration strategies that support workforce mobility, ensure compliance with immigration laws, and minimize disruption to business operations. Our multilingual team provides culturally sensitive service to clients from around the world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">Our Client-Centered Approach</h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
              We believe exceptional legal service requires more than just technical expertise—it demands a deep understanding of your business and a commitment to your success
            </p>
          </div>

          {/* Mobile: Horizontal scroll, Desktop: Grid */}
          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 shadow-sm border border-slate-100 flex-shrink-0 w-[85vw] md:w-auto snap-center"
            >
              <div className="text-5xl font-bold text-amber-700 mb-4">01</div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Strategic Thinking</h3>
              <p className="text-slate-600 leading-relaxed">
                We don't just provide legal advice—we serve as strategic partners who understand your business objectives and help you achieve them. Our attorneys take time to understand your industry, challenges, and goals to provide counsel that advances your interests.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 shadow-sm border border-slate-100 flex-shrink-0 w-[85vw] md:w-auto snap-center"
            >
              <div className="text-5xl font-bold text-amber-700 mb-4">02</div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Practical Solutions</h3>
              <p className="text-slate-600 leading-relaxed">
                We focus on practical, business-oriented solutions rather than theoretical legal analysis. Our recommendations consider real-world constraints, commercial realities, and your risk tolerance to deliver advice you can actually implement.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 shadow-sm border border-slate-100 flex-shrink-0 w-[85vw] md:w-auto snap-center"
            >
              <div className="text-5xl font-bold text-amber-700 mb-4">03</div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Responsive Service</h3>
              <p className="text-slate-600 leading-relaxed">
                Business moves fast, and legal counsel must keep pace. We're available when you need us, responsive to your inquiries, and proactive in identifying issues before they become problems. Our clients appreciate our accessibility and commitment to service.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">Why Choose Us</h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              What sets us apart in the legal industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              'Proven track record of successful outcomes',
              'Personalized attention to every client',
              'Deep industry expertise across multiple sectors',
              'Strategic thinking and innovative solutions',
              'Transparent communication and billing',
              'Available 24/7 for urgent matters',
              'Strong relationships with regulatory bodies',
              'Commitment to ethical practice',
              'Results-driven approach'
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="flex items-start gap-3"
              >
                <CheckCircle className="h-6 w-6 text-amber-700 flex-shrink-0 mt-1" />
                <p className="text-lg text-slate-700">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats - Removed as per client request */}

      {/* CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Ready to Work With Us?</h2>
          <p className="text-xl text-slate-600 mb-8">
            Let's discuss how we can help you achieve your legal objectives.
          </p>
          <button
            onClick={openLeadForm}
            className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-4 font-serif text-lg transition-all hover:tracking-wider shadow-lg hover:shadow-xl"
            data-testid="about-cta-button"
          >
            Schedule Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
